import { call, put, takeLatest, select, delay } from 'redux-saga/effects';
import { Auth } from 'aws-amplify';
import isEmpty from 'lodash/isEmpty';
import i18n from 'i18next';
import moment from 'moment-timezone';
import hotToast from 'react-hot-toast';

import { ProfileLanguage, FullUser } from 'types';

import {
  doCheckIsAuthorized,
  doCheckUser,
  doLogin,
  doLogout,
} from 'store/actionCreators/auth';
// import { doLoginChat } from 'store/actionCreators/chat';
import { getCurrentUserSubscription } from 'store/actionCreators/subscriptions';
import {
  getCheckUserCache,
  getCurrentIdToken,
  getFullAuthUser,
} from 'store/selectors/auth';
import { checkUserForSignIn, getCurrentUser } from 'services/auth';
import { setHeaders, clearHeaders } from 'services';
import { setAnalyticsUserId } from 'utils/analytics';
import { removeAllToasts, toast } from 'utils/toastAbstraction';
import {
  doGetNotifications,
  doGetTotalChatCount,
  doGetUnRespondedEventsCount,
  editProfile,
  doChangeProfileSettings,
} from '../actionCreators/profile';
import {
  doClearCommunities,
  doRequestCommunities,
} from '../actionCreators/communities';
import { getCommunitiesLoaded } from '../selectors/communities';
import {
  commonGetStorageCommunity,
  commonClearStorageCommunity,
} from 'utils/commonStorage';
import {
  clearReturnUrl,
  consumeReturnUrl,
  navigateToPath,
} from 'utils/authRedirect';

const CHECK_USER_CACHE_TTL_MS = 60 * 1000;

// Helper function to extract Google Analytics parameters
const getGoogleAnalyticsParams = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const gaParams = new URLSearchParams();

  for (const [key, value] of searchParams.entries()) {
    if (key.startsWith('_g')) {
      gaParams.append(key, value);
    }
  }

  return gaParams.toString();
};

// Helper function to detect browser language and map to supported language
const detectBrowserLanguage = (): string => {
  const availableLanguages = (i18n.options['languages'] as string[]) || [
    'en',
    'de',
    'fr',
    'it',
    'es',
    'ru',
  ];
  const fallBackLanguage = i18n.options.fallbackLng?.[0] || 'en';

  // Helper to map detected languages to supported ones
  const mapToSupportedLanguage = (detectedList: string[]): string => {
    if (!Array.isArray(detectedList)) return fallBackLanguage;
    for (let lang of detectedList) {
      lang = lang.toLowerCase();
      if (lang.includes('de') || lang.includes('-ch')) return 'de';
      if (lang.startsWith('fr')) return 'fr';
      if (lang.startsWith('it')) return 'it';
      if (lang.startsWith('es')) return 'es';
      if (lang.startsWith('ru')) return 'ru';
      if (lang.startsWith('en')) return 'en';
      // Direct match to available languages
      if (availableLanguages.includes(lang)) return lang;
      // Try base language (e.g., 'de-CH' -> 'de')
      const baseLang = lang ? lang.split('-')[0] : '';
      if (availableLanguages.includes(baseLang)) return baseLang;
    }
    return fallBackLanguage;
  };

  // Get browser languages - convert readonly array to mutable
  const browserLangs = Array.from(navigator.languages || [navigator.language]);
  return mapToSupportedLanguage(browserLangs);
};

function* checkUser({ payload }: ReturnType<typeof doCheckUser>) {
  try {
    const normalizedEmail = payload.email.toLowerCase();
    const cache = yield select(getCheckUserCache);
    const cached = cache?.[normalizedEmail];

    const now = Date.now();
    if (
      cached?.data &&
      typeof cached.expiresAt === 'number' &&
      now < cached.expiresAt
    ) {
      const fetchedAt =
        typeof cached.fetchedAt === 'number' ? cached.fetchedAt : now;
      yield put(
        doCheckUser.success({
          email: normalizedEmail,
          data: cached.data,
          fetchedAt,
          expiresAt: now + CHECK_USER_CACHE_TTL_MS,
        }),
      );
      yield call(payload.onSuccess, cached.data);
      return;
    }

    const data = yield call(checkUserForSignIn, normalizedEmail);
    yield put(
      doCheckUser.success({
        email: normalizedEmail,
        data,
        fetchedAt: now,
        expiresAt: now + CHECK_USER_CACHE_TTL_MS,
      }),
    );
    yield call(payload.onSuccess, data);
  } catch (error) {
    yield put(doCheckUser.failure(error));
    if (payload?.onFailure) {
      yield call(payload.onFailure, error);
    }
  }
}

function* checkIsAuthorized(payload: ReturnType<typeof doCheckIsAuthorized>) {
  try {
    let cognitoSession = yield Auth.currentSession();
    const currentUserToken = yield select(getCurrentIdToken);

    if (
      new Date(cognitoSession.idToken?.payload.exp * 1000).getTime() <=
      new Date().getTime()
    ) {
      const cognitoUser = yield Auth.currentAuthenticatedUser();

      yield new Promise((resolve, reject) => {
        cognitoUser.refreshSession(
          cognitoSession.refreshToken,
          (err, session) => {
            if (err) reject();

            const { idToken } = session;
            cognitoSession = session;

            setHeaders(idToken.jwtToken);
            resolve(null);
          },
        );
      });
    } else if (
      !currentUserToken ||
      cognitoSession.idToken.jwtToken !== currentUserToken ||
      !cognitoSession.idToken?.payload?.id
    ) {
      yield setHeaders(cognitoSession.idToken.jwtToken);
    }

    if (
      !currentUserToken ||
      cognitoSession.idToken.jwtToken !== currentUserToken ||
      !cognitoSession.idToken?.payload?.id
    ) {
      const user = yield call(
        getCurrentUser,
        cognitoSession.idToken.payload.id || cognitoSession.idToken.payload.sub,
      );

      yield put(
        doCheckIsAuthorized.success({
          ...user,
          idToken: cognitoSession.idToken.jwtToken,
        }),
      );
      setAnalyticsUserId(user.id);
      if (payload?.payload?.onSuccess) {
        yield payload.payload.onSuccess(user, cognitoSession);
      }
      yield put(doGetNotifications({ userId: user.id }));
      yield put(doGetTotalChatCount({ userId: user.id }));
      yield put(doGetUnRespondedEventsCount({ userId: user.id }));

      const timezone = moment.tz.guess();

      if (
        user.profile &&
        (!user.profile.timeZone || user.profile.timeZone !== timezone) &&
        timezone
      ) {
        yield put(
          editProfile({
            data: {
              timeZone: timezone,
            },
            id: user.id,
          }),
        );
      }

      // Determine which language to use
      let languageToUse = user.settings.language;

      // If user has no language set or it's the default 'en', detect browser language
      if (!languageToUse || languageToUse === 'en') {
        const detectedLanguage = detectBrowserLanguage();
        // Only use detected language if it's different from 'en' (to avoid unnecessary updates)
        if (detectedLanguage !== 'en') {
          languageToUse = detectedLanguage;
          // Update user's language preference in backend
          yield put(
            doChangeProfileSettings.trigger({
              userId: user.id,
              data: { language: detectedLanguage as ProfileLanguage },
            }),
          );
        }
      }

      if (i18n.language !== languageToUse) {
        yield delay(0);
        yield i18n.changeLanguage(languageToUse);
      }
    } else {
      const user = yield select(getFullAuthUser);
      yield put(doCheckIsAuthorized.success(user));
      if (user?.id) {
        setAnalyticsUserId(user.id);
      }
      if (payload?.payload?.onSuccess) {
        yield payload.payload.onSuccess(user, cognitoSession);
      }
      // Determine which language to use
      let languageToUse = user.settings.language;

      // If user has no language set or it's the default 'en', detect browser language
      if (!languageToUse || languageToUse === 'en') {
        const detectedLanguage = detectBrowserLanguage();
        // Only use detected language if it's different from 'en' (to avoid unnecessary updates)
        if (detectedLanguage !== 'en') {
          languageToUse = detectedLanguage;
          // Update user's language preference in backend
          yield put(
            doChangeProfileSettings.trigger({
              userId: user.id,
              data: { language: detectedLanguage as ProfileLanguage },
            }),
          );
        }
      }

      if (i18n.language !== languageToUse) {
        yield delay(0);
        yield i18n.changeLanguage(languageToUse);
      }
    }
    yield put(getCurrentUserSubscription.trigger());

    const communitiesLoaded = yield select(getCommunitiesLoaded);
    if (!communitiesLoaded) {
      yield put(doRequestCommunities());
    }
  } catch (e) {
    setAnalyticsUserId(null);
    yield put(doCheckIsAuthorized.failure());
    if (payload?.payload?.onFailure) {
      yield call(payload.payload.onFailure);
    }
    if (e.code && e.message) {
      yield toast(
        i18n.t([`errors.${e.code}`, 'errors.errorToast'], {
          message: e.message,
        }),
        {
          appearance: 'error',
          autoDismiss: true,
        },
      );
    }
  }
}

function* login({ payload }: ReturnType<typeof doLogin>) {
  try {
    yield put(doLogin.request());
    const cognitoUser = yield Auth.currentUserInfo();
    if (isEmpty(cognitoUser)) {
      yield Auth.signIn(payload.email, payload.password);
      yield put(doLogin.success());

      // Get Google Analytics parameters
      const gaParams = getGoogleAnalyticsParams();

      // Trigger authorization check with navigation callback
      yield put(
        doCheckIsAuthorized.trigger({
          onSuccess: (user: FullUser) => {
            // A deep link the user just tried to open outranks the remembered
            // community, which is only a bookmark of where they last were.
            const returnUrl = consumeReturnUrl();

            if (returnUrl) {
              navigateToPath(returnUrl);
              return;
            }

            const storageCommunity = commonGetStorageCommunity();

            let newUrl = `/profile${gaParams ? `?${gaParams}` : ''}`;

            if (storageCommunity) {
              // Basic validation: must start with /community/ or /communities/
              const communityMatch = storageCommunity.match(
                /^\/communit(?:y|ies)\/([^\/]+)/,
              );

              if (communityMatch) {
                const communityId = communityMatch[1];
                const hasAccess = user.communities.some(
                  (c) => c.id === communityId,
                );

                if (hasAccess) {
                  newUrl = storageCommunity;
                } else {
                  // Community doesn't exist or user no longer has access
                  commonClearStorageCommunity();
                  // toast(i18n.t('errors.communityAccessDenied'), {
                  //   appearance: 'error',
                  //   autoDismiss: true,
                  // });
                }
              } else if (storageCommunity !== '/profile') {
                // Invalid or corrupted data (not a community URL and not /profile)
                commonClearStorageCommunity();
              }
            }

            navigateToPath(newUrl);
          },
        }),
      );
    }
  } catch (error) {
    yield put(doLogin.failure());

    if (payload.onFailure) {
      yield call(payload.onFailure, error);
    }
  }
}

function* logout() {
  try {
    removeAllToasts();
    hotToast?.dismiss();
    setAnalyticsUserId(null);
    clearReturnUrl();
    yield Auth.signOut();
    yield clearHeaders();
    yield put(doClearCommunities.trigger());
    yield put(doCheckIsAuthorized.failure());
    navigateToPath('/', { replace: true });
  } catch (e) {
    yield put(doLogout.failure(e));
  }
}

export default function* () {
  yield takeLatest(doLogout.TRIGGER, logout);
  yield takeLatest(doCheckIsAuthorized.TRIGGER, checkIsAuthorized);
  yield takeLatest(doLogin.TRIGGER, login);
  yield takeLatest(doCheckUser.TRIGGER, checkUser);
}
